import type { Ability } from '@/lib/permissions';
import { isSuperAdminRole } from '@/lib/roles';
import type { SupportSessionInfo } from '@/lib/session';
import type { User } from '@/lib/types';

/*
 * Who may start a SUPPORT SESSION — acting as another person to reproduce what
 * they see — and about whom.
 *
 * ONE module for TWO entry points. The owner asked for both a row action on
 * `/users` and a search on `/settings`; these rules are the same in both, and
 * two copies would agree on the day they were written and drift on the first
 * edit. Same split as `user-groups.ts` beside `InlineUserGroups.tsx`: the pure
 * model lives here, the dialog is a component file, and neither has to break
 * react-refresh's component-only-export rule to share with the other.
 *
 * **Nothing here touches the wire.** The transport is
 * `useAuth().startSupportSession(userId, reason)` — read that method before
 * changing anything here.
 */

/*
 * ⚠️ **`isSuperAdminRole` MOVED TO `@/lib/roles` ON 8 SEPTEMBER 2026.** It lived
 * here, private, under a comment saying to promote it the day a second caller
 * appeared. `BuildSwitcher` is that caller. The behaviour is unchanged — the
 * promoted version enumerates the permitted slug exactly as this one did, and
 * fails closed for `null`, `undefined` and any role the API adds later.
 *
 * Kept as a note rather than deleted silently: this file reads as though it
 * still owns the predicate, and a reader who greps for the definition here and
 * finds nothing has no way to tell a move from a deletion.
 */

/**
 * Why this reader may not start a support session as this person, or `null`
 * when they may.
 *
 * ⚠️ **Every branch is a VIEW of a rule the SERVER owns, and every one of them
 * is still handled as a refusal in the dialog.** A role can be taken away under
 * an access token that lasts over five days, and the target's role can change
 * between this render and the request. The control is the optimisation; the
 * refusal is the guarantee. Same split as `canDelete` on `/users`.
 */
export function supportSessionDenial(
  ability: Ability,
  actor: User | null | undefined,
  target: User,
  /** The session already in progress, from `useAuth().supportSession`. */
  running: SupportSessionInfo | null,
): string | null {
  /*
   * ⚠️ **A DIRECT TIER CHECK, deliberately — one of the few places it is the
   * right answer.** The house rule is to gate on the `ability` object and never
   * on `user.role`, and this obeys it: the question is not "may this account
   * write?" but "is this account in the top tier?", which is precisely what
   * `ability.isSuperAdmin` answers. The rule still lives in
   * `@/lib/permissions`; what is unusual is only that no `ModuleSlug` stands
   * behind it.
   *
   * ⚠️ **`isSuperAdmin`, never `isAdmin`.** The two are one keystroke apart,
   * `isAdmin` is true for an ordinary `admin`, and everywhere else in this app
   * the two slugs are interchangeable — so the wrong one silently hands a whole
   * tier a capability the owner scoped to one. `/permission-sets` is the
   * precedent: that area is super-admin-only in this app, stricter than the API
   * itself, and gates on this same flag.
   *
   * ⚠️ **And there is no `denialReason()` to borrow.** That helper takes a
   * `ModuleSlug`, and support sessions are not a permission module — inventing
   * one would make this ask `/permissions/me` a question nobody has ever put to
   * the server, which is the stop `SettingsPage` and `/targets` both take. So
   * these sentences are written out, once, here.
   */
  /*
   * 🔴 2026-10-01: the `support-sessions` module (only the super-admin set holds
   * it today) — `POST /support-sessions` is `requireGrant('support-sessions',
   * 'create')`. *SUPERSEDED, the paragraph above: `ability.isSuperAdmin`, and
   * "support sessions are not a permission module".*
   */
  if (!ability.canCreate('support-sessions')) {
    return 'Your permission set does not include this. An administrator can change that.';
  }
  /*
   * No nesting. `startSupportSession` rejects outright — the base token is what
   * a support session is layered over, and a second layer would shadow the
   * first with nothing able to unwind it — so this names the reason rather than
   * letting somebody meet a rejection with no sentence attached.
   */
  if (running !== null) {
    return `You are already acting as ${running.actingAs.name}. End that session before starting another.`;
  }
  /*
   * The row somebody clicks first while testing. Its own refusal rather than
   * folded into the tier one above: "you are not allowed" would be a false
   * statement about a reader who is allowed, just not here.
   */
  if (actor != null && target.id === actor.id) {
    return 'This is your own account — you are already signed in as this person.';
  }
  if (isSuperAdminRole(target.role)) {
    return 'A super administrator’s account cannot be entered this way.';
  }
  return null;
}

/**
 * Whether a support-session control belongs on the screen at all.
 *
 * Separate from {@link supportSessionDenial} on purpose. The per-row rules
 * there produce a DISABLED control beside its reason, so the reader learns the
 * rule instead of hunting a button that is not there. The TIER is a different
 * question: a reader who is not a super administrator has no business meeting
 * the affordance on every row of a page every role can read, so the whole
 * control is withheld from them rather than held down eleven times.
 */
export function canOfferSupportSessions(ability: Ability): boolean {
  // 2026-10-01: the `support-sessions` module. *SUPERSEDED: `isSuperAdmin`.*
  return ability.canCreate('support-sessions');
}
